import type { GameAction, InputFrame, Player, Vec2 } from "@golazo/shared";
import { vec } from "../core/math.js";
import type { InputSource } from "./keyboard.js";

/**
 * Golpear con el raton: clic izquierdo, hacia el cursor.
 *
 * Con teclado la direccion del golpeo sale de WASD, o sea de ocho; con el
 * raton sale del grado exacto, que es la forma de apuntar de cualquier juego de
 * PC con vista cenital. Un toque es un pase al sitio que señalas (a un
 * compañero, al hueco) y mantener carga el tiro, igual que la tecla.
 *
 * No toca la simulacion ni el protocolo: mientras el boton esta pulsado, esta
 * fuente pone la direccion del cursor como `move` del fotograma, que es de
 * donde la simulacion ya lee la mira al cargar (`lockKickDirection`). Fuera
 * del clic no dice nada y el teclado sigue mandando. Mover el raton mientras
 * cargas hace lo mismo que girar el stick: la mira lo sigue despacio y comba.
 */
export interface MouseAimOptions {
  /** Cursor (coordenadas de la ventana) -> punto del campo. */
  toWorld(clientX: number, clientY: number, canvas: HTMLCanvasElement): Vec2 | null;
  /** El jugador que llevas: el golpeo se apunta desde el. */
  player(): Player | null;
  /** Pulsar o soltar cambia el fotograma: en red se envia al momento. */
  onChange?: () => void;
  target?: Window;
}

export function createMouseAim(options: MouseAimOptions): InputSource {
  const target = options.target ?? window;
  const held = new Set<GameAction>();
  const pressed = new Set<GameAction>();
  const move = vec();
  const frame: InputFrame = { move, held, pressed };

  let down = false;
  let cursor: { x: number; y: number; canvas: HTMLCanvasElement } | null = null;

  const isMouseOnCanvas = (event: PointerEvent): event is PointerEvent & { target: HTMLCanvasElement } =>
    event.pointerType === "mouse" && event.target instanceof HTMLCanvasElement;

  const onMove = (event: PointerEvent): void => {
    if (!isMouseOnCanvas(event)) return;
    cursor = { x: event.clientX, y: event.clientY, canvas: event.target };
  };
  const onDown = (event: PointerEvent): void => {
    if (event.button !== 0 || !isMouseOnCanvas(event)) return;
    event.preventDefault();
    cursor = { x: event.clientX, y: event.clientY, canvas: event.target };
    down = true;
    pressed.add("kick");
    options.onChange?.();
  };
  const onUp = (event: PointerEvent): void => {
    if (event.button !== 0 || !down) return;
    down = false;
    options.onChange?.();
  };
  // Soltar fuera de la ventana no llega: al perder el foco se suelta.
  const onBlur = (): void => {
    down = false;
    pressed.clear();
  };

  target.addEventListener("pointermove", onMove);
  target.addEventListener("pointerdown", onDown);
  target.addEventListener("pointerup", onUp);
  target.addEventListener("blur", onBlur);

  return {
    consume(): InputFrame {
      move.x = 0;
      move.y = 0;
      held.clear();
      if (down) held.add("kick");
      if ((down || pressed.size > 0) && cursor) {
        const me = options.player();
        const aim = options.toWorld(cursor.x, cursor.y, cursor.canvas);
        if (me && aim) {
          const dx = aim.x - me.pos.x;
          const dy = aim.y - me.pos.y;
          const len = Math.hypot(dx, dy);
          if (len > 1) {
            move.x = dx / len;
            move.y = dy / len;
          }
        }
      }
      return frame;
    },
    endTick(): void {
      pressed.clear();
    },
    dispose(): void {
      target.removeEventListener("pointermove", onMove);
      target.removeEventListener("pointerdown", onDown);
      target.removeEventListener("pointerup", onUp);
      target.removeEventListener("blur", onBlur);
      held.clear();
      pressed.clear();
    },
  };
}
